import React from "react";
import { defineMessages, FormattedMessage as T, useIntl } from "react-intl";
import { makeStyles, Theme } from "@material-ui/core/styles";
import IconButton from "@material-ui/core/IconButton";
import Typography from "@material-ui/core/Typography";
import Skeleton from "@material-ui/lab/Skeleton";
import CheckBoxOutlineBlankOutlinedIcon from "@material-ui/icons/CheckBoxOutlineBlankOutlined";
import CheckBoxOutlinedIcon from "@material-ui/icons/CheckBoxOutlined";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import BorderAllIcon from "@material-ui/icons/BorderAll";
import BurstModeIcon from "@material-ui/icons/BurstMode";
import PhotoCameraIcon from "@material-ui/icons/PhotoCamera";
import MapIcon from "@material-ui/icons/Map";
import clsx from "clsx";

import {
  setTarget,
  updateSize,
  showLayerSwitcher,
  hideLayerSwitcher,
} from "../../../lib/carto/main";
import LapiImg from "../../LapiImg";
import Preview from "./Preview";
import SelectionManager, {
  SelectionManagerEventType,
} from "../../../lib/selection-manager";
import Tooltip from "@material-ui/core/Tooltip";
import moment from "moment";

import useCityConfig from "../../../hooks/useCityConfig";

const useStyles = makeStyles<Theme, Pick<ImageControlProps, "invalidColor">>(
  (theme: Theme) => ({
    /* Styles applied to the root element. */
    root: {
      width: "100%",
      height: "100%",
      display: "flex",
      flexDirection: "column",
      "& >:nth-child(4)": {
        flex: 1,
        justifyContent: "flex-end",
      },
    },
    imageControlHeader: {
      height: 48,
      display: "flex",
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
      padding: 8,
      backgroundColor: theme.palette.imageControl.main,
      color: theme.palette.common.white,
      borderRadius: theme.shape.borderRadius,
      "& >*": {
        flex: 1,
      },
      "& >:nth-child(2)": {
        textAlign: "center",
      },
      "& >:nth-child(3)": {
        textAlign: "end",
      },
    },
    title: {
      display: "flex",
      alignItems: "center",
      "& :first-child": {
        marginRight: 8,
      },
    },
    imageControlBody: {
      flex: 1, //none
      position: "relative",
      overflowY: "auto",
      padding: 8,
    },
    wrapperWithIntrinsicRatio: {
      position: "relative",
      // paddingBottom: "75%", // 4:3
      margin: "0px 20%",
      height: "100%", //0
      // overflow: "hidden",
      "&:hover": {
        cursor: "all-scroll",
      },
      backgroundColor: theme.palette.common.black,
    },
    elementToStretch: {
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
    },
    image: {
      borderRadius: theme.shape.borderRadius,
    },
    checkboxContainer: {
      display: "flex",
      flexDirection: "row",
      justifyContent: "flex-end",
      position: "absolute",
      top: 16,
      left: -32,
    },
    checkboxContainerMini: {
      top: 8,
      left: -16,
      zIndex: 2,
    },
    checkboxButton: {
      width: 64,
      padding: 0,
      color: theme.palette.primary.dark,
      boxShadow: theme.shadows[2],
      "&:hover": {
        backgroundColor: "initial",
      },
    },
    checkboxButtonMini: {
      width: 32,
    },
    checkboxButtonSelected: {
      color: theme.palette.common.white,
    },
    checkboxIcon: {
      width: "100%",
      height: "100%",
      backgroundColor: theme.palette.common.white,
      borderRadius: "50%",
      padding: 8,
    },
    checkboxIconMini: {
      padding: 6,
    },
    checkboxIconSelected: {
      backgroundColor: "green",
    },
    chevronLeft: {
      position: "absolute",
      top: "calc(50% - 50px)",
      left: 8,
      height: 96,
      width: 96,
      color: theme.palette.primary.main,
      "& > * > *": {
        height: 96,
        width: 96,
      },
      "&:hover": {
        color: theme.palette.primary.dark,
      },
    },
    chevronRight: {
      position: "absolute",
      top: "calc(50% - 50px)",
      right: 8,
      height: 96,
      width: 96,
      color: theme.palette.primary.main,
      "& > * > *": {
        height: 96,
        width: 96,
      },
      "&:hover": {
        color: theme.palette.primary.dark,
      },
    },
    imgContainer: {
      flex: 5,
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      padding: 3,
      overflow: "hidden",
      "& > *": {
        margin: "0px 16px",
      },
    },
    noImageAvailable: {
      width: "100%",
      height: "100%",
      display: "flex",
      flexDirection: "row",
      justifyContent: "center",
      alignItems: "center",
    },
    skeletonImage: {
      borderRadius: theme.shape.borderRadius,
    },
    skeletonSquareContainer: {
      flex: 5,
      display: "flex",
      flexDirection: "row",
      justifyContent: "center",
      alignItems: "center",
    },
    skeletonSquareWrapper: {
      padding: 8,
    },
    skeletonSquare: {
      width: 130,
      height: 130,
    },
    imageControlfooter: {
      display: "flex",
      alignItems: "center",
      height: 156,
      padding: 8,
    },
    chevronMiniLeft: {
      marginRight: 8,
      height: 64,
      width: 64,
      color: theme.palette.primary.main,
      "& > * > *": {
        height: 64,
        width: 64,
      },
      "&:hover": {
        color: theme.palette.primary.dark,
      },
    },
    chevronMiniRight: {
      marginLeft: 8,
      marginRight: 8,
      height: 64,
      width: 64,
      color: theme.palette.primary.main,
      "& > * > *": {
        height: 64,
        width: 64,
      },
      "&:hover": {
        color: theme.palette.primary.dark,
      },
    },
    modeButton: {
      backgroundColor: "white",
      color: theme.palette.primary.main,
      borderRadius: 4,
      height: 32,
      width: 32,
      "&:hover": {
        backgroundColor: theme.palette.grey[300],
        transition: "all 150ms ease-in-out",
      },
    },
    mapPreview: {
      position: "relative",
      flex: 3,
      height: "100%",
      "& >:last-child": {
        height: "100%",
      },
    },
    mapContainer: {
      position: "relative",
      width: "100%",
      height: "100%",
    },
    expandControl: {
      position: "absolute",
      top: ".5em",
      right: ".5em",
      zIndex: 1,
    },
    /* Styles applied to the map. */
    map: {
      width: "100%",
      height: "100%",
      "& .ol-viewport": {
        borderRadius: theme.shape.borderRadius,
      },
      "& .history-control": {
        top: "calc(.5em + 2px + 1.375em + 1px + 1.375em + 2px + 1em)",
        left: ".5em",
      },
      "& .ol-rotate": {
        top: ".5em",
        right: "calc(.5em + 2px + 1.375em + 2px + 1em)",
      },
      "& .ol-layerswitcher": {
        top: "calc(.5em + 2px + 1.375em + 2px + 1em)",
        right: ".5em",
        "& :first-child": {
          backgroundColor: "rgba(0,60,136,0.5)",
        },
      },
      "& .ol-layerswitcher button": {
        backgroundColor: "rgba(0,60,136,0.5)",
      },
      "& .ol-layerswitcher button:hover, & .ol-layerswitcher button:focus": {
        backgroundColor: "rgba(0,60,136,0.7)",
      },
    },
    mapPlaceholder: {
      width: "100%",
      height: "100%",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      backgroundColor: theme.palette.grey[300],
      borderRadius: 4,
      "& >:first-child": {
        height: 70,
        width: 70,
        color: "white",
      },
    },
    inBoxButton: {
      top: 10,
      left: 40,
    },
    validityBadge: {
      width: 24,
      height: 24,
      border: "thin solid white",
      borderRadius: "50%",
    },
    full: {
      flex: 1,
    },
    imagesItem: {
      position: "relative",
      width: 130,
      minWidth: 130,
      height: 130,
      display: "flex",
      alignItems: "center",
      backgroundColor: theme.palette.common.black,
      "& >:first-child": {
        width: "100%",
      },
    },
    pointerOnHover: {
      "&:hover": {
        cursor: "pointer",
      },
    },
    placeholderImage: {
      height: "100%",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      color: theme.palette.grey[300],
      "& >:first-child": {
        height: "20%",
        width: "20%",
      },
    },
    checked: {
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      backgroundColor: "rgba(255, 255, 255, 0.4)",
      zIndex: 1,
    },
    invalid: {
      backgroundColor: ({ invalidColor }) => {
        return invalidColor || theme.palette.error.light;
      },
    },
  })
);

const msg = defineMessages({
  title: {
    id: "imageControl.title",
    description: "Title of the image control.",
    defaultMessage: "Context",
  },
  restoreButtonLabel: {
    id: "imageControl.modeButton.label",
    description: "Message displayed to change the pictures view.",
    defaultMessage: "Change View",
  },
  invalid: {
    id: "imageControl.invalid",
    description: "Tell the user the ticket control is invalid.",
    defaultMessage: "Invalid",
  },
  noImageAvailable: {
    id: "imageControl.noImageAvailable",
    description: "Message displayed when there is no image available.",
    defaultMessage: "No image available",
  },
  openMapButtonLabel: {
    id: "imageControl.openMapButtonLabel",
    description: "Message displayed when mouse over button map.",
    defaultMessage: "Display map",
  },
  closeMapButtonLabel: {
    id: "imageControl.closeMapButtonLabel",
    description:
      "Message displayed when mouse over button map when map is open.",
    defaultMessage: "Reduce map",
  },
});

export type ImageWithThumbnail = {
  image: string;
  thumbnail: string;
};

export type ImageControlProps = React.DetailedHTMLProps<
  React.HTMLAttributes<HTMLDivElement>,
  HTMLDivElement
> & {
  images?: ImageWithThumbnail[];
  selectionLimit?: number;
  selectionOverride?: string[];
  date?: string;
  valid?: boolean;
  invalidColor?: string;
  onImageSelectionChange?(selectedImages?: ImageWithThumbnail[]): void;
  onReset?(): void;
};

function ImageControl(props: ImageControlProps) {
  ///////////////// PROPS, HOOKS AND STATE SETUP////////////////////////////////

  const {
    className,
    images,
    selectionLimit,
    selectionOverride,
    date,
    valid,
    invalidColor,
    onImageSelectionChange,
    onReset,
    ...other
  } = props;
  const { formatMessage: t } = useIntl();
  const { cityConfig } = useCityConfig();
  const [activeImage, setActiveImage] = React.useState<ImageWithThumbnail>();
  const backgroundMode = React.useRef<"review" | "preview">("review");
  const [mode, setMode] = React.useState<"review" | "preview" | "map">(
    "review"
  );
  const selectionManagerRef = React.useRef(
    new SelectionManager<ImageWithThumbnail>()
  );
  const [selectionDisabled, setSelectionDisabled] = React.useState(true);

  ///////////////// CSS ////////////////////////////////////////////////////////

  const classes = useStyles({ invalidColor });

  const rootClasses = React.useMemo(() => {
    return clsx(classes.root, className);
  }, [className, classes.root]);

  const validityBadgeClasses = React.useMemo(
    () => clsx(classes.validityBadge, classes.invalid),
    [classes.invalid, classes.validityBadge]
  );

  ///////////////// INIT AND SIDE EFFECT(S) ////////////////////////////////////

  const isSelected = React.useCallback((img: ImageWithThumbnail) => {
    const selectionManager = selectionManagerRef.current;

    const selected = selectionManager.isSelected(img);

    if (selected === undefined) return false;

    return selected;
  }, []);

  const handleSelectionManagerChange = React.useCallback(() => {
    if (images === undefined || images.length === 0) return;

    const selectedImages = images.filter((img) => isSelected(img));

    if (selectionLimit === undefined) {
      setSelectionDisabled(false); // No selection limit means the selection is enabled.
    } else {
      setSelectionDisabled(selectedImages.length >= selectionLimit);
    }

    if (onImageSelectionChange !== undefined) {
      onImageSelectionChange(selectedImages);
    }
  }, [images, isSelected, onImageSelectionChange, selectionLimit]);

  // Manage the image selection change.
  React.useEffect(() => {
    const selectionManager = selectionManagerRef.current;

    selectionManager.on(
      SelectionManagerEventType.CHANGE,
      handleSelectionManagerChange
    );

    return () => {
      selectionManager.off(
        SelectionManagerEventType.CHANGE,
        handleSelectionManagerChange
      );
    };
  }, [handleSelectionManagerChange]);

  // When the images props changes, adapt internal state.
  React.useEffect(() => {
    const selectionManager = selectionManagerRef.current;

    if (images === undefined || images.length === 0) {
      setActiveImage(undefined);
      setSelectionDisabled(true);
      return;
    }

    setActiveImage(images[0]);
    setSelectionDisabled(false);
    images.forEach((img) => selectionManager.add(img));
    setMode(backgroundMode.current);
  }, [images]);

  // When the selectionOverride props changes, adjust the selection.
  React.useEffect(() => {
    if (images === undefined) return;

    const selectionManager = selectionManagerRef.current;
    let imagesToSelect = images;
    if (
      cityConfig?.review?.fullPictures === true &&
      selectionOverride === undefined
    ) {
      if (
        cityConfig.review.fps.maxPicturesNumber &&
        cityConfig.review.fps.maxPicturesNumber < images.length
      ) {
        imagesToSelect = imagesToSelect.slice(
          0,
          cityConfig.review.fps.maxPicturesNumber
        );
      }
      setTimeout(() => {
        imagesToSelect.forEach((img) => {
          selectionManager.select(img);
        });
      }, 100);

      return;
    }

    if (selectionOverride === undefined) return;

    images.forEach((img) => {
      selectionOverride?.forEach((ovr) => {
        if (img.image === ovr) {
          selectionManager.select(img);
        }
      });
    });
  }, [
    images,
    selectionOverride,
    cityConfig?.review.fullPictures,
    cityConfig?.review.fps.maxPicturesNumber,
  ]);

  React.useEffect(() => {
    if (mode === "map") {
      setTarget("fullMap");
      showLayerSwitcher();
      updateSize();
    } else if (mode === "preview" || mode === "review") {
      setTarget("map");
      hideLayerSwitcher();
      updateSize();
    }
  }, [mode]);

  ///////////////// WORKFLOW ///////////////////////////////////////////////////

  const handleOpenMapButtonClick = React.useCallback(
    (_event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      if (mode !== "map") {
        backgroundMode.current = mode;
      }
      setMode(mode === "map" ? backgroundMode.current : "map");
    },
    [backgroundMode, mode]
  );

  const handleModeButtonClick = React.useCallback(
    (_event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      const nextMode = mode === "review" ? "preview" : "review";

      backgroundMode.current = nextMode;
      setMode(nextMode);
    },
    [mode]
  );

  const handleCheckboxClick = React.useCallback(
    (img: ImageWithThumbnail) =>
      (_event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        const selectionManager = selectionManagerRef.current;
        selectionManager.toggle(img);
        _event.stopPropagation();
      },
    []
  );

  const handleArrowClick = React.useCallback(
    (direction: "right" | "left") =>
      (_event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        if (images === undefined || images.length === 0) return;

        const activeImageIndex = images.findIndex((img) => img === activeImage);
        if (activeImageIndex === -1) return;

        let nextActiveImageIndex = activeImageIndex;
        if (direction === "right") {
          if (++nextActiveImageIndex === images.length)
            nextActiveImageIndex = 0;
        } else if (direction === "left") {
          if (--nextActiveImageIndex === -1)
            nextActiveImageIndex = images.length - 1;
        }
        let selectedElement = document.getElementById(
          nextActiveImageIndex.toString()
        );
        var offsetLeft = selectedElement ? selectedElement.offsetLeft : 0;
        let container: HTMLElement = document.getElementById(
          "imgContainer"
        ) as HTMLElement;

        if (container !== undefined && offsetLeft) {
          container.scrollLeft = offsetLeft - 115;
        }
        setActiveImage(images[nextActiveImageIndex]);
      },
    [activeImage, images]
  );

  const handleClickedImage = React.useCallback(
    (img: ImageWithThumbnail) =>
      (_event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        if (mode === "map") return;

        setActiveImage(img);
      },
    [mode]
  );

  return (
    <>
      <div className={rootClasses} {...other}>
        <div className={classes.imageControlHeader}>
          <Typography variant="h6" noWrap className={classes.title}>
            {valid === false && (
              <Tooltip title={t(msg.invalid)}>
                <div className={validityBadgeClasses} />
              </Tooltip>
            )}
            <T {...msg.title} />
          </Typography>
          <Typography variant="h6" noWrap>
            {date ? (
              <>
                {moment(date).format("L")} {moment(date).format("LTS")}
              </>
            ) : (
              <Skeleton width="150px" />
            )}
          </Typography>
          <span>
            <Tooltip title={t(msg.restoreButtonLabel)}>
              <IconButton
                disableRipple
                className={classes.modeButton}
                aria-label={t(msg.restoreButtonLabel)}
                color="inherit"
                onClick={handleModeButtonClick}
              >
                {mode === "review" ? <BorderAllIcon /> : <BurstModeIcon />}
              </IconButton>
            </Tooltip>
          </span>
        </div>
        <div className={classes.imageControlBody}>
          {images === undefined || activeImage === undefined ? (
            <>
              <div className={classes.placeholderImage}>
                <PhotoCameraIcon />
              </div>
            </>
          ) : images.length === 0 ? (
            <div className={classes.noImageAvailable}>
              <Typography>
                <T {...msg.noImageAvailable} />
              </Typography>
            </div>
          ) : (
            <>
              {
                {
                  review: (
                    <>
                      <IconButton
                        className={classes.chevronLeft}
                        onClick={handleArrowClick("left")}
                      >
                        <ChevronLeftIcon />
                      </IconButton>
                      <div className={classes.wrapperWithIntrinsicRatio}>
                        <LapiImg
                          className={clsx(
                            classes.elementToStretch,
                            classes.image
                          )}
                          src={activeImage.image}
                          size={500}
                          zoom={true}
                        />

                        <div className={classes.checkboxContainer}>
                          <IconButton
                            className={clsx(
                              classes.checkboxButton,
                              isSelected(activeImage)
                                ? classes.checkboxButtonSelected
                                : undefined
                            )}
                            disabled={
                              selectionDisabled && !isSelected(activeImage)
                            }
                            onClick={handleCheckboxClick(activeImage)}
                          >
                            {isSelected(activeImage) ? (
                              <CheckBoxOutlinedIcon
                                className={clsx(
                                  classes.checkboxIcon,
                                  classes.checkboxIconSelected
                                )}
                              />
                            ) : (
                              <CheckBoxOutlineBlankOutlinedIcon
                                className={classes.checkboxIcon}
                              />
                            )}
                          </IconButton>
                        </div>
                      </div>
                      <IconButton
                        className={classes.chevronRight}
                        onClick={handleArrowClick("right")}
                      >
                        <ChevronRightIcon />
                      </IconButton>
                    </>
                  ),
                  preview: (
                    <Preview
                      images={images}
                      selectionDisabled={selectionDisabled}
                      isSelected={isSelected}
                      onActiveImageChange={setActiveImage}
                      onCheckboxClick={handleCheckboxClick}
                    />
                  ),
                  map: (
                    <div className={classes.mapContainer}>
                      <Tooltip
                        title={
                          mode === "map"
                            ? t(msg.closeMapButtonLabel)
                            : t(msg.openMapButtonLabel)
                        }
                      >
                        <div
                          className={clsx(
                            classes.expandControl,
                            "ol-unselectable ol-control"
                          )}
                        >
                          <button onClick={handleOpenMapButtonClick}>
                            {mode === "map" ? "↘" : "↖"}
                          </button>
                        </div>
                      </Tooltip>
                      <div id="fullMap" className={classes.map} />
                    </div>
                  ),
                }[mode]
              }
            </>
          )}
        </div>
        <div className={classes.imageControlfooter}>
          {images === undefined || activeImage === undefined ? (
            <div className={classes.skeletonSquareContainer}>
              <div className={classes.skeletonSquareWrapper}>
                <Skeleton className={classes.skeletonSquare} variant="rect" />
              </div>
              <div className={classes.skeletonSquareWrapper}>
                <Skeleton className={classes.skeletonSquare} variant="rect" />
              </div>
              <div className={classes.skeletonSquareWrapper}>
                <Skeleton className={classes.skeletonSquare} variant="rect" />
              </div>
            </div>
          ) : (
            mode !== "preview" && (
              <>
                <IconButton
                  className={classes.chevronMiniLeft}
                  disabled={mode === "map"}
                  onClick={handleArrowClick("left")}
                >
                  <ChevronLeftIcon />
                </IconButton>
                <div className={classes.imgContainer} id="imgContainer">
                  {images.map((img, index) => (
                    <div
                      id={index.toString()}
                      key={img.image}
                      className={clsx(
                        classes.imagesItem,
                        mode !== "map" && classes.pointerOnHover
                      )}
                      onClick={handleClickedImage(img)}
                    >
                      <LapiImg src={img.thumbnail} />
                      <div
                        className={clsx(
                          classes.checkboxContainer,
                          classes.checkboxContainerMini
                        )}
                      >
                        <IconButton
                          className={clsx(
                            classes.checkboxButton,
                            classes.checkboxButtonMini,
                            isSelected(img)
                              ? classes.checkboxButtonSelected
                              : undefined
                          )}
                          disabled={
                            mode === "map" ||
                            (selectionDisabled && !isSelected(img))
                          }
                          onClick={handleCheckboxClick(img)}
                        >
                          {isSelected(img) ? (
                            <CheckBoxOutlinedIcon
                              className={clsx(
                                classes.checkboxIcon,
                                classes.checkboxIconMini,
                                classes.checkboxIconSelected
                              )}
                            />
                          ) : (
                            <CheckBoxOutlineBlankOutlinedIcon
                              className={clsx(
                                classes.checkboxIcon,
                                classes.checkboxIconMini
                              )}
                            />
                          )}
                        </IconButton>
                        {/* Stop event propagation when checkbox disabled */}
                        {selectionDisabled && !isSelected(img) && (
                          <div
                            style={{
                              position: "absolute",
                              width: 36,
                              height: "100%",
                              padding: 0,
                            }}
                            onClick={(event) => event.stopPropagation()}
                          />
                        )}
                      </div>
                      {activeImage === img && (
                        <div className={classes.checked}></div>
                      )}
                    </div>
                  ))}
                </div>
                <IconButton
                  className={classes.chevronMiniRight}
                  disabled={mode === "map"}
                  onClick={handleArrowClick("right")}
                >
                  <ChevronRightIcon />
                </IconButton>
              </>
            )
          )}
          <div className={classes.mapPreview}>
            <Tooltip
              title={
                mode === "map"
                  ? t(msg.closeMapButtonLabel)
                  : t(msg.openMapButtonLabel)
              }
            >
              <div
                className={clsx(
                  classes.expandControl,
                  "ol-unselectable ol-control"
                )}
              >
                <button onClick={handleOpenMapButtonClick}>
                  {mode === "map" ? "↘" : "↖"}
                </button>
              </div>
            </Tooltip>
            {mode === "map" ? (
              <div className={classes.mapPlaceholder}>
                <MapIcon />
              </div>
            ) : (
              <div id="map" className={classes.map} />
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export default ImageControl;
